// Lightweight translation loader with cache, fallback and interpolation

const DEFAULT_LOCALE = 'pt-BR';

const translationsCache = {};

function getInitialLocale() {
  try {
    const stored = localStorage.getItem('lang');
    if (stored) return stored;
  } catch (_) {}
  if (typeof window !== 'undefined') {
    // Allows configuring via public/config.js as window.lang = 'pt-BR'
    if (window.lang && typeof window.lang === 'string') return window.lang;
  }
  return DEFAULT_LOCALE;
}

async function loadLocale(locale) {
  if (translationsCache[locale]) return translationsCache[locale];
  try {
    // dynamic import enables code-splitting per locale
    const mod = await import(
      /* webpackChunkName: "lang-[request]" */ `./${locale}.json`
    );
    translationsCache[locale] = mod.default || mod;
    return translationsCache[locale];
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        `[lang] Failed to load locale ${locale}. Falling back to ${DEFAULT_LOCALE}.`,
        err
      );
    }
    if (locale !== DEFAULT_LOCALE) {
      return loadLocale(DEFAULT_LOCALE);
    }
    translationsCache[DEFAULT_LOCALE] = {};
    return translationsCache[DEFAULT_LOCALE];
  }
}

function interpolate(template, params) {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    return Object.prototype.hasOwnProperty.call(params, key)
      ? String(params[key])
      : `{${key}}`;
  });
}

function resolveKey(obj, path) {
  return path
    .split('.')
    .reduce(
      (acc, part) => (acc && acc[part] != null ? acc[part] : undefined),
      obj
    );
}

export async function createTranslator(localeParam) {
  const locale = localeParam || getInitialLocale();
  const current = await loadLocale(locale);
  const fallback = await loadLocale(DEFAULT_LOCALE);

  const t = (key, params) => {
    let value = resolveKey(current, key);
    if (value == null) value = resolveKey(fallback, key);
    if (value == null) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(`[lang] Missing key: ${key}`);
      }
      return key;
    }
    if (typeof value === 'object') {
      // naive pluralization: expects keys .one/.other
      const count = params && (params.count ?? params.n);
      const form = count === 1 ? 'one' : 'other';
      value = value[form] ?? value.other ?? value.one;
    }
    return interpolate(String(value), params);
  };

  return { t, locale };
}

export function setLocale(locale) {
  try {
    localStorage.setItem('lang', locale);
  } catch (_) {}
}

export function getLocale() {
  return getInitialLocale();
}
